import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EVENTO_REINICIO_DO_TOUR, TOUR_STORAGE_KEY } from '@/lib/tourBoasVindas';

/**
 * A pessoa ja esta na tela que a etapa descreve?
 *
 * Compara por subarvore, e nao por igualdade. `/dashboard` apenas redireciona
 * para `/dashboard/alertas-kpis`, entao quem esta olhando o dashboard nunca tem
 * `pathname === '/dashboard'` — e a comparacao exata dava sempre "ainda nao
 * chegou".
 */
function estaNaTela(pathname: string, rota: string) {
  return pathname === rota || pathname.startsWith(`${rota}/`);
}

type TourStep = {
  title: string;
  description: string;
  icon: string;
  targetLabel: string;
  actionPath?: string;
};

const tourSteps: TourStep[] = [
  {
    title: 'Bem-vindo ao Central Check!',
    description:
      'Vamos te guiar pelos principais recursos da plataforma em poucos passos. Você gerencia condomínios, alvarás, treinamentos e muito mais num só lugar.',
    icon: 'ri-building-line',
    targetLabel: 'Visão geral',
  },
  {
    title: 'Dashboard',
    description:
      'Aqui você vê KPIs, alertas e o calendário geral. Os cards de Treinamentos e Alvarás mostram barras coloridas: verde (OK), âmbar (prestes a vencer) e vermelho (vencido).',
    icon: 'ri-dashboard-line',
    targetLabel: 'Dashboard',
    actionPath: '/dashboard',
  },
  {
    title: 'Condomínios',
    description:
      'Cadastre seus condomínios, torres, unidades e funcionários. Cada condomínio tem abas para licenças, manutenções, alvarás e mais.',
    icon: 'ri-building-line',
    targetLabel: 'Condomínios',
    actionPath: '/condominios',
  },
  {
    title: 'Saúde e Pendências',
    description:
      'Acompanhe o índice de conformidade e veja as pendências de cadastro e segurança. Cada item tem um link direto pra tela onde você resolve.',
    icon: 'ri-shield-check-line',
    targetLabel: 'Saúde e Pendências',
    actionPath: '/saude-pendencias',
  },
  {
    title: 'Central de Ajuda',
    description:
      'Sempre que precisar, acesse Ajuda no menu lateral (abaixo do seu Perfil). Lá estão as orientações passo a passo e as perguntas frequentes.',
    icon: 'ri-question-line',
    targetLabel: 'Ajuda',
    actionPath: '/ajuda',
  },
];

export default function WelcomeTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const done = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!done) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  // "Refazer tour", na Central de Ajuda, chega por aqui — ver `reiniciarTour`.
  useEffect(() => {
    const reabrir = () => {
      setStep(0);
      setDontShowAgain(false);
      setVisible(true);
    };

    window.addEventListener(EVENTO_REINICIO_DO_TOUR, reabrir);
    return () => window.removeEventListener(EVENTO_REINICIO_DO_TOUR, reabrir);
  }, []);

  const finish = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    }
    setVisible(false);
  }, [dontShowAgain]);

  // A etapa leva para a tela que ela descreve.
  //
  // Antes existia um botao por etapa que navegava e **encerrava** o tour: quem
  // clicasse em "Ir para o Dashboard" no segundo passo nunca via os outros
  // tres. Ler sobre uma tela sem ve-la, ou ve-la ao preco de abandonar o tour,
  // eram as duas unicas opcoes.
  //
  // Agora avancar navega, e o tour continua por cima da tela real — inclusive
  // ao voltar, que refaz o caminho na ordem inversa. A primeira etapa nao tem
  // rota: e a de boas-vindas, e ela nao descreve tela nenhuma.
  //
  // A navegacao acontece aqui, no clique, e nao num efeito que observa o passo.
  // O efeito precisava de `navigate` nas dependencias, e a identidade de
  // `navigate` muda a cada troca de `pathname` — entao ele reexecutava a cada
  // navegacao, inclusive as que nao eram dele. Na etapa do Dashboard isso
  // desfazia o redirecionamento da propria rota: `/dashboard` so renderiza um
  // `<Navigate>` para `/dashboard/alertas-kpis`, o efeito reagia a essa troca
  // mandando de volta para `/dashboard`, e a aplicacao parava numa rota que nao
  // desenha nada — tela vazia atras do tour, que dali nao saia do lugar.
  const irParaPasso = useCallback(
    (destino: number) => {
      setStep(destino);

      const rota = tourSteps[destino]?.actionPath;
      if (rota && !estaNaTela(pathname, rota)) navigate(rota);
    },
    [navigate, pathname],
  );

  const next = useCallback(() => {
    if (step < tourSteps.length - 1) {
      irParaPasso(step + 1);
    } else {
      finish();
    }
  }, [step, irParaPasso, finish]);

  if (!visible) return null;

  const current = tourSteps[step];
  // Avancar navega, entao o botao diz para onde — sem isso a tela troca sem
  // aviso e a pessoa perde a referencia de onde estava.
  //
  // Só quando o destino é outro. Anunciar "Próximo: Dashboard" para quem já
  // está no dashboard promete uma mudança que não acontece, e faz o tour
  // parecer travado.
  const proximo = tourSteps[step + 1];
  const proximoDestino =
    proximo?.actionPath && !estaNaTela(pathname, proximo.actionPath)
      ? proximo.targetLabel
      : undefined;
  const isFirst = step === 0;
  const isLast = step === tourSteps.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />

      {/* Mobile indicator dots */}
      <div className="absolute top-4 left-0 right-0 flex justify-center gap-1.5">
        {tourSteps.map((_, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full transition-all ${idx === step ? 'bg-background-50 w-6' : 'bg-background-50/40'}`}
          />
        ))}
      </div>

      <div className="relative bg-background-50 rounded-xl border border-background-200/70 shadow-2xl w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden animate-in">
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-background-200/60">
          <div
            className="h-full bg-primary-500 transition-all duration-300 rounded-r"
            style={{ width: `${((step + 1) / tourSteps.length) * 100}%` }}
          />
        </div>

        <div className="p-6 pt-7">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mb-4 mx-auto">
            <i className={`${current.icon} text-2xl text-primary-600`}></i>
          </div>

          {/* Step counter */}
          <p className="text-xs text-foreground-400 text-center mb-2 font-medium">
            Passo {step + 1} de {tourSteps.length} — {current.targetLabel}
          </p>

          {/* Title */}
          <h3 className="text-lg font-bold text-foreground-900 text-center mb-2">
            {current.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-foreground-600 text-center leading-relaxed">
            {current.description}
          </p>

          {/* O botao de acao por etapa saiu: a navegacao acontece ao avancar, e
              um botao que fizesse a mesma coisa duplicaria o caminho. Com ele
              foi embora tambem o `actionLabel` de cada etapa, que nao tinha
              mais quem o lesse — o destino agora aparece no proprio "Proximo". */}

          {/* Don't show again checkbox */}
          <div className="mt-5 flex justify-center">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${dontShowAgain ? 'bg-primary-500 border-primary-500' : 'border-foreground-300 bg-transparent'}`}
                >
                  {dontShowAgain && (
                    <i className="ri-check-line text-[10px] text-background-50 font-bold"></i>
                  )}
                </div>
              </div>
              <span className="text-xs text-foreground-500">Não mostrar novamente</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-background-200/70 flex items-center justify-between bg-background-100/30">
          {!isFirst ? (
            <button
              onClick={() => irParaPasso(step - 1)}
              className="text-sm text-foreground-500 hover:text-foreground-700 font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line"></i> Voltar
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={finish}
            className="text-sm text-foreground-400 hover:text-foreground-600 font-medium transition-colors whitespace-nowrap"
          >
            Pular tour
          </button>

          <button
            onClick={next}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap transition-colors ${
              isLast
                ? 'text-primary-600 hover:text-primary-700'
                : 'text-primary-600 hover:text-primary-700'
            }`}
          >
            {isLast ? 'Concluir' : proximoDestino ? `Próximo: ${proximoDestino}` : 'Próximo'}
            <i className={`${isLast ? 'ri-check-line' : 'ri-arrow-right-s-line'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}
